/**
 * Two-way route bridge between this app (running in an iframe) and the host page.
 *
 * A host page cannot read a cross-origin iframe's location, so the only way it
 * can mirror our route into its own address bar is if we tell it. Likewise, when
 * someone hits back/forward or opens a deep link, the host has to tell us.
 *
 * Protocol (plain postMessage, both directions):
 *
 *   app  -> host   { type: "app2food:ready",    path }   once, when we mount
 *   app  -> host   { type: "app2food:route",    path }   on every route change
 *   host -> app    { type: "app2food:navigate", path }   deep link, back/forward
 *
 * `path` is always basename-relative ("/menu", not "/olo/11076/menu"), because
 * react-router strips the basename from useLocation().
 *
 * No feedback loop: a host-initiated navigate makes us emit a route message
 * back, but the host compares it with the URL it already has and skips the
 * write, so the exchange terminates after one round.
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** Cross-origin access to window.parent can throw; if it does, we are framed. */
function isFramed(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

/**
 * Messages arrive from whatever page embeds us, which we do not control, so the
 * path is untrusted input. Only internal absolute paths get through.
 *
 * "//evil.com" is the case that matters: it looks like a path but is
 * protocol-relative, and would take the frame off our origin.
 */
export function sanitizeFramePath(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  if (raw.length > 2048) return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//")) return null;
  if (raw.includes("\\")) return null;

  // Reject control characters, which have no business in a route.
  for (let i = 0; i < raw.length; i++) {
    if (raw.charCodeAt(i) < 0x20 || raw.charCodeAt(i) === 0x7f) return null;
  }

  return raw;
}

export const FrameSync = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // --- outbound: mirror our route into the host's address bar ---------------
  useEffect(() => {
    if (!isFramed()) return;

    // targetOrigin "*" because the client domain is not known at build time.
    // The payload is a menu path, not user data. Tighten this once
    // store_domains (docs/embed/API-SPEC.md) can supply the parent origin.
    window.parent.postMessage(
      { type: "app2food:route", path: location.pathname + location.search },
      "*",
    );
  }, [location]);

  // --- ready handshake ------------------------------------------------------
  // The iframe's load event fires before React has routed, so the host cannot
  // use it to hand over a deep link. This is the reliable signal.
  useEffect(() => {
    if (!isFramed()) return;
    window.parent.postMessage(
      { type: "app2food:ready", path: location.pathname + location.search },
      "*",
    );
    // Mount-only on purpose: "ready" must fire exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- inbound: host asks us to navigate ------------------------------------
  useEffect(() => {
    if (!isFramed()) return;

    function onMessage(event: MessageEvent) {
      // Only our embedder may drive navigation. This rejects other frames and
      // any script on the host page that is not the widget.
      if (event.source !== window.parent) return;

      const data = event.data as { type?: unknown; path?: unknown } | null;
      if (!data || typeof data !== "object") return;
      if (data.type !== "app2food:navigate") return;

      const path = sanitizeFramePath(data.path);
      if (!path) return;
      if (path === location.pathname + location.search) return;

      navigate(path);
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [navigate, location]);

  return null;
};

export default FrameSync;
